import { FieldDefinition, FieldBuilder, MenuCategories, MenuSubcategories, MENU_CATEGORY_KEY, MENU_SUBCATEGORY_KEY, MENU_NAME_SUFFIX } from '@zendra/shared';
import { ReasonService } from '../proxy/general/controllers/reason.service';
import { EnergySourceService } from '../proxy/general/controllers/energy-source.service';
import { ModelService } from '../proxy/general/controllers/model.service';
import { VehicleTechnologyService } from '../proxy/general/controllers/vehicle-technology.service';
import { BrandService } from '../proxy/general/controllers/brand.service';
import { TruckLogicService } from '../proxy/general/controllers/truck-logic.service';
import { TractionTypeService } from '../proxy/general/controllers/traction-type.service';
import { Truck } from '@general/truck/truck';
import { LightVehicle } from '@general/light-vehicle/light-vehicle';
import { TruckCategoryService, VehicleTypeService } from '@general/proxy/general/controllers';
import { VendorService, InternalOrderService } from '@controlling/proxy/controlling/controllers';

FieldBuilder.setNamespace('General');

export const VEHICLE_FIELDS: FieldDefinition[] = [
  FieldBuilder
    .text('name', 'Entity.Vehicle.Name')
    .required()
    .minLength(1)
    .maxLength(50)
    .build(),
  FieldBuilder
    .text('code', 'Entity.Vehicle.Code')
    .minLength(1)
    .maxLength(20)
    .build(),
  FieldBuilder
    .foreignKey('deactivationReasonId', 'Entity.Machine.DeactivationReason', ReasonService)
    .displayFromNested('deactivationReason.name')
    .sortBy('deactivationReason.name')
    .build(),
  FieldBuilder
    .date('deactivationDate', 'Entity.Vehicle.DeactivationDate')
    .build(),
  FieldBuilder
    .checkbox('isActive', 'Entity.Vehicle.IsActive')
    .build(),
  FieldBuilder
    .text('sapCode', 'Entity.Vehicle.SapCode')
    .minLength(1)
    .maxLength(20)
    .build(),
  FieldBuilder // TODO: when the orchestrator is implemented, refactor this nested logic into it
    .foreignKey('vendorId', 'Entity.Vendor', VendorService)
    .display('fantasyName')
    .keyField('id')
    .build(),
  FieldBuilder // TODO: when the orchestrator is implemented, refactor this nested logic into it
    .foreignKey('internalOrderId', 'Entity.InternalOrder', InternalOrderService)
    .display('name')
    .keyField('id')
    .build(),
  FieldBuilder
    .text('notes', 'Entity.Vehicle.Notes')
    .minLength(5)
    .maxLength(300)
    .build(),
  FieldBuilder
    .text('interfaceStatus', 'Entity.Vehicle.InterfaceStatus')
    .minLength(5)
    .maxLength(300)
    .build(),
  FieldBuilder
    .text('plate', 'Entity.Vehicle.Plate')
    .required()
    .minLength(1)
    .maxLength(20)
    .build(),
  FieldBuilder
    .checkbox('hasGps', 'Entity.Vehicle.HasGps')
    .build(),
  FieldBuilder
    .number('horsePower', 'Entity.Vehicle.HorsePower')
    .decimal(2,18)
    .build(),
  FieldBuilder
    .checkbox('co2Emisions', 'Entity.Vehicle.Co2Emisions')
    .build(),
  FieldBuilder
    .foreignKey('energySourceId', 'Entity.EnergySource', EnergySourceService)
    .displayFromNested('energySource.name')
    .sortBy('energySource.name')
    .build(),
  FieldBuilder
    .foreignKey('modelId', 'Entity.Model', ModelService)
    .displayFromNested('model.name')
    .sortBy('model.name')
    .build(),
  FieldBuilder
    .foreignKey('vehicleTechnologyId', 'Entity.VehicleTechnology', VehicleTechnologyService)
    .displayFromNested('vehicleTechnology.name')
    .sortBy('vehicleTechnology.name')
    .build(),
  FieldBuilder
    .foreignKey('brandId', 'Entity.Brand', BrandService)
    .displayFromNested('brand.name')
    .sortBy('brand.name')
    .build(),
  FieldBuilder
    .foreignKey('truckLogicId', 'Entity.TruckLogic', TruckLogicService)
    .displayFromNested('truckLogic.name')
    .sortBy('truckLogic.name')
    .build(),
  FieldBuilder
    .foreignKey('truckCategoryId', 'Entity.TruckCategory', TruckCategoryService)
    .displayFromNested('truckCategory.name')
    .sortBy('truckCategory.name')
    .build(),
  FieldBuilder
    .foreignKey('tractionTypeId', 'Entity.TractionType', TractionTypeService)
    .displayFromNested('tractionType.name')
    .sortBy('tractionType.name')
    .build(),
  FieldBuilder
    .foreignKey('vehicleTypeId', 'Entity.VehicleType', VehicleTypeService)
    .required()
    .displayFromNested('vehicleType.name')
    .sortBy('vehicleType.name')
    .build(),
];

export const VEHICLE_CONFIG = {
  entityName: 'Vehicle',
  localizationKey: 'General::Entity.Vehicle',
  fields: VEHICLE_FIELDS,
  permission: 'GeneralMachinesAndVehicles.Vehicles',
  showInMenu: true,
  menuCategory: MENU_CATEGORY_KEY + MenuCategories.General + MENU_NAME_SUFFIX,
  menuSubcategory: MENU_SUBCATEGORY_KEY + MenuSubcategories.MachinesAndVehicles + MENU_NAME_SUFFIX,
  getChildren: (row: any, allChildren: any[]) => {
    const vehicleCategory = row?.vehicleType.vehicleCategory;
    if (!vehicleCategory) {
      return [];
    }
    const vehicleCategoryName = vehicleCategory.entityName || vehicleCategory.name;
    if (!vehicleCategoryName) {
      return [];
    }
    const lowerCategoryName = vehicleCategoryName.toLowerCase();
    return allChildren.filter(child => child.entityName.toLowerCase() === lowerCategoryName);
  },
  children: [
    {
      entityName: 'Truck',
      foreignKeyField: 'vehicleId',
      localizationKey: 'General::Entity.Truck',
      component: Truck,
    },
    {
      entityName: 'LightVehicle',
      foreignKeyField: 'vehicleId',
      localizationKey: 'General::Entity.LightVehicle',
      component: LightVehicle,
    },
  ]
};
